import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";

/**
 * Bottom inset for soft keyboard + safe docking above system nav.
 *
 * Prefer Capacitor Keyboard height when IME is open (windowSoftInputMode=adjustNothing).
 * Fall back to visualViewport overlap. Never double-count.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    let disposed = false;
    let capHeight = 0;
    let keyboardOpen = false;
    const handles: { remove: () => Promise<void> }[] = [];

    const viewportOverlap = (): number => {
      const vv = window.visualViewport;
      if (!vv) return 0;
      return Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
    };

    const apply = () => {
      if (disposed) return;
      const overlap = viewportOverlap();

      if (keyboardOpen && capHeight > 0) {
        // Capacitor reports raw keyboard height; clamp to a sane max.
        const capped = Math.min(
          Math.round(capHeight),
          Math.round(window.innerHeight * 0.65)
        );
        setInset(Math.max(capped, overlap > 24 ? Math.round(overlap) : capped));
        return;
      }

      // Viewport already shrunk for IME without Capacitor events.
      if (overlap >= 24) {
        setInset(Math.round(overlap));
        return;
      }

      if (!keyboardOpen) setInset(0);
    };

    const onVv = () => apply();
    window.visualViewport?.addEventListener("resize", onVv);
    window.visualViewport?.addEventListener("scroll", onVv);
    window.addEventListener("resize", onVv);
    apply();

    if (
      Capacitor.isNativePlatform() &&
      Capacitor.isPluginAvailable("Keyboard")
    ) {
      void (async () => {
        try {
          await Keyboard.setResizeMode({ mode: KeyboardResize.None });
        } catch {
          /* older / unimplemented — visualViewport fallback */
        }
        try {
          const show = await Keyboard.addListener("keyboardWillShow", (info) => {
            keyboardOpen = true;
            capHeight = info.keyboardHeight;
            apply();
          });
          const didShow = await Keyboard.addListener(
            "keyboardDidShow",
            (info) => {
              keyboardOpen = true;
              capHeight = info.keyboardHeight;
              apply();
            }
          );
          const hide = await Keyboard.addListener("keyboardWillHide", () => {
            keyboardOpen = false;
            capHeight = 0;
            if (!disposed) setInset(0);
          });
          const didHide = await Keyboard.addListener("keyboardDidHide", () => {
            keyboardOpen = false;
            capHeight = 0;
            if (!disposed) setInset(0);
          });
          handles.push(show, didShow, hide, didHide);
        } catch {
          /* visualViewport only */
        }
      })();
    }

    return () => {
      disposed = true;
      window.visualViewport?.removeEventListener("resize", onVv);
      window.visualViewport?.removeEventListener("scroll", onVv);
      window.removeEventListener("resize", onVv);
      for (const h of handles) void h.remove();
    };
  }, []);

  return inset;
}

/**
 * CSS bottom offset that keeps chrome above IME or system navigation.
 * When keyboard closed: uses env(safe-area-inset-bottom) via CSS var.
 * When keyboard open: pixel inset from useKeyboardInset.
 */
export function selectionToolbarBottomStyle(keyboardInset: number): {
  bottom: string;
  paddingBottom: string;
} {
  if (keyboardInset > 0) {
    return {
      bottom: `${keyboardInset}px`,
      paddingBottom: "0.35rem",
    };
  }
  // Extra nav padding beyond safe-area (3-button nav often under-reports)
  return {
    bottom: "0px",
    paddingBottom: "calc(0.45rem + max(var(--safe-bottom), 12px))",
  };
}
